// src/views/pick/PickView.tsx
/* eslint-disable max-len */
import React, {
  FC, useEffect, useState, useCallback,
} from 'react';
import './PickView.less';
import './slice/slice';
import { useSelector } from 'react-redux';
import FixWidthContent from '../../components/FixWidthContent';
import withTagManager from '../../hooks/withTagManager';
import { PictureOfWord } from '../../components/Pick/PictureOfWord';
import InfoButton from '../../components/InfoButton/InfoButton';
import RulesOfTheGame from './RulesOfTheGame';
import { BASE_API_URL } from '../../services/ApiService';
import { getActiveList, getUserData } from '../auth/slice/selectors';
import EndGameInfo from '../../components/EndGameInfo/EndgameInfo';
import useGoToRoute from '../../hooks/useGoToRoute';
import { Path } from '../../constants/Path';
import useAudioPlayer from '../../hooks/useAudioPlayer';
import { RightSideButtons } from '../../components/RightSideButtons/RightSideButtons';
import { UnsplashCredits } from '../../components/UnsplashCredits/UnsplashCredits';
import { EditTermButton } from '../../components/EditTermModal/EditTermButton';
import { usePolishAudio } from '../../hooks/usePolishAudio';
import { PolishAudioButton } from '../../components/PolishAudioButton/PolishAudioButton';
import { Roles } from '../../constants/Roles';
import { cleanTextForTTS } from '../../utils/textBracketSanitize';
import { getListWordId } from '../../utils/gameWords';
import WordCounter from '../../components/WordCounter';
import { EditListButton } from '../../components/EditListButton/EditListButton';
import { FitText } from '../../components/FitText/FitText';
import useActivityTracking from '../../hooks/useActivityTracking';
import { ActivityGame, recordAnswer } from '../../services/activity';

// Funkcja pomocnicza do tasowania
function shuffleArray<T>(array: T[]): T[] {
  return [...array].sort(() => 0.5 - Math.random());
}

const generateChoices = (currentWord: any, allWords: any[], currentIndex: number) => {
  const removedCurrentWord = allWords.filter((word) => word.id !== allWords[currentIndex].id);
  const uniqueByTerm = [...new Map(
    removedCurrentWord.map((word) => [word.term, word]),
  ).values()];
  const shuffled = shuffleArray(uniqueByTerm);
  const differentTerm = shuffled.filter((word) => word.term !== currentWord.term);
  const fiveWords = differentTerm.slice(0, 5);

  return shuffleArray([...fiveWords, currentWord]);
};

export const PickView: FC = () => {
  const activeList = useSelector(getActiveList);
  const { words } = activeList;
  const audioPlayer = useAudioPlayer();
  const goToNext = useGoToRoute(Path.PAIRING);

  useActivityTracking(ActivityGame.PICK);

  const {
    playPolish,
    togglePlayPolish,
    playPolishAudio,
  } = usePolishAudio({ storageKey: 'pick_playPolish' });

  const [stateWordsCopy, setStateWordsCopy] = useState(() => shuffleArray([...words]));
  const [wordOnScreen, setWordOnScreen] = useState<number>(0);
  const [mistakeStyle, setMistakeStyle] = useState<boolean | string>(false);
  const [correctStyle, setCorrectStyle] = useState<boolean | string>(false);
  const [listEndInfo, setListEndInfo] = useState(false);

  const user = useSelector(getUserData);
  const canEditTerms = user?.role === Roles.TEACHER;

  const [currentSixWords, setCurrentSixWords] = useState<any[]>([]);
  const lastWordOnScreenRef = React.useRef<number>(-1);

  const currentWord = stateWordsCopy[wordOnScreen];
  const numberOfWords = stateWordsCopy.length;
  const index = wordOnScreen + 1;
  const isLastWord = index === numberOfWords;

  useEffect(() => {
    if (wordOnScreen !== lastWordOnScreenRef.current) {
      if (currentWord) {
        setCurrentSixWords(generateChoices(currentWord, stateWordsCopy, wordOnScreen));
        lastWordOnScreenRef.current = wordOnScreen;
      }
    } else {
      setCurrentSixWords((prev) => prev.map((word) => {
        const updated = stateWordsCopy.find((w) => w.id === word.id);

        return updated || word;
      }));
    }
  }, [wordOnScreen, stateWordsCopy, currentWord]);

  // Funkcja dodająca słowo do powtórki
  const addWordToRepeat = useCallback(() => {
    const wordToRepeat = stateWordsCopy[wordOnScreen];
    const wordTerm = wordToRepeat.term;

    const remainingWords = stateWordsCopy.slice(wordOnScreen + 1);
    const alreadyHasRepeat = remainingWords.some((word) => word.term === wordTerm);

    if (alreadyHasRepeat) {
      return;
    }

    let needsSeparator = false;
    if (wordOnScreen < stateWordsCopy.length - 1) {
      const nextWordTerm = stateWordsCopy[wordOnScreen + 1].term;
      needsSeparator = (nextWordTerm === wordTerm);
    } else {
      needsSeparator = true;
    }

    const wordsToAdd = [];

    if (needsSeparator) {
      let randomWordObj;
      let attempts = 0;
      const maxAttempts = 50;

      do {
        let randomIndex;
        if (attempts < 30) {
          const preferredRange = Math.ceil(words.length * 0.7);
          randomIndex = Math.floor(Math.random() * preferredRange);
        } else {
          randomIndex = Math.floor(Math.random() * words.length);
        }

        randomWordObj = words[randomIndex];
        attempts += 1;
      } while (
        attempts < maxAttempts
        && randomWordObj.term === wordTerm
      );

      const randomCopy = { ...randomWordObj };
      randomCopy.index = stateWordsCopy.length + wordsToAdd.length;
      randomCopy.id = randomCopy.index.toString();
      randomCopy.listWordId = getListWordId(randomWordObj);

      wordsToAdd.push(randomCopy);
    }

    const repeatCopy = { ...wordToRepeat };
    repeatCopy.index = stateWordsCopy.length + wordsToAdd.length;
    repeatCopy.id = repeatCopy.index.toString();
    repeatCopy.listWordId = getListWordId(wordToRepeat);

    wordsToAdd.push(repeatCopy);

    setStateWordsCopy([
      ...stateWordsCopy,
      ...wordsToAdd,
    ]);
  }, [stateWordsCopy, wordOnScreen, words]);

  // Funkcja odtwarzająca sekwencję audio
  const playAudioSequence = useCallback((term: string, definition: string, onComplete?: () => void) => {
    const cleanTerm = cleanTextForTTS(term);

    if (!cleanTerm) {
      if (onComplete) onComplete();

      return;
    }

    const termUrl = `${BASE_API_URL}/v1/tts?q=${encodeURIComponent(cleanTerm)}`;

    // Sprawdź czy polskie audio jest włączone
    audioPlayer.play(termUrl, () => {
      // B. Następnie odtwórz polskie tłumaczenie (załatwia to ulepszony hook)
      setTimeout(() => {
        // Zamiast budować URL i sprawdzać playPolishRef.current,
        // wywołujemy nasz ulepszony hook, który obsługuje:
        // - Czy grać (playPolish check)
        // - Czyszczenie tekstu (cleanTextForTTS)
        // - Callback po zakończeniu (onComplete logic)
        playPolishAudio(definition, () => {
          // Callback dla polskiego audio. Jeśli polskie było wyłączone,
          // ten callback i tak zostanie wywołany natychmiast przez hooka.
          if (onComplete) {
            setTimeout(onComplete, 300);
          }
        });
      }, 200);
    });
  }, [audioPlayer, playPolishAudio]);

  const handleClick = useCallback((id: any, term: any) => {
    // Po dobrej odpowiedzi lektor czyta słowo – do pokazania następnego ignorujemy kliknięcia
    if (correctStyle !== false) return;

    const isCorrect = currentWord?.term === term;

    recordAnswer(isCorrect, currentWord?.term);

    if (isCorrect && !isLastWord) {
      // Poprawny wybór - nie ostatnie słowo
      setMistakeStyle(false);
      setCorrectStyle(id);

      playAudioSequence(currentWord.term, currentWord.definition, () => {
        setWordOnScreen((prev) => prev + 1);
      });
    } else if (isCorrect && isLastWord) {
      // Poprawny wybór - ostatnie słowo
      setCorrectStyle(id);

      playAudioSequence(currentWord.term, currentWord.definition, () => {
        setListEndInfo(true);
      });
    } else {
      // Błędny wybór
      setCorrectStyle(false);
      setMistakeStyle(id);
      addWordToRepeat();

      const clickedWord = currentSixWords.find((word) => word.id === id);
      if (clickedWord) {
        playAudioSequence(clickedWord.term, clickedWord.definition);
      }
    }
  }, [correctStyle, currentWord, isLastWord, playAudioSequence, addWordToRepeat, currentSixWords]);

  // Powtórzenie gry
  const repeatGame = useCallback(() => {
    setTimeout(() => {
      setWordOnScreen(0);
      setCorrectStyle(false);
      setMistakeStyle(false);
      setListEndInfo(false);
      setStateWordsCopy(shuffleArray([...words]));
      lastWordOnScreenRef.current = -1; // Reset ref
    }, 500);
  }, [words]);

  // Reset stylu correct przy zmianie wordOnScreen
  useEffect(() => {
    setCorrectStyle(false);
  }, [wordOnScreen]);

  return (
    <>
      <FixWidthContent className="pick-main">
        {!listEndInfo && (
        <WordCounter
          current={wordOnScreen + 1}
          total={stateWordsCopy.length}
        />
        )}
        {/* Na telefonie trzymanym poziomo obrazek z podpisem z lewej, a karty z prawej (PickView.less) */}
        <div className="enter">
          {!listEndInfo && (
            <div className="pick-stage">
              <div className="pick-picture">
                <PictureOfWord
                  id={currentWord?.id}
                  image={currentWord?.image}
                  alt={currentWord?.term}
                  caption={currentWord?.definition}
                />
              </div>
              <div className="pick_container">
                {/* Długi zwrot dostaje mniejszą czcionkę, żeby zmieścił się w karcie (na telefonie) */}
                {currentSixWords.map((word: any) => (
                  <FitText
                    className={`picks ${word?.id === mistakeStyle ? 'mistake' : ''} ${word?.id === correctStyle ? 'correct' : ''}`}
                    key={word.id}
                    id={word?.id}
                    onClick={() => handleClick(word.id, word?.term)}
                    // Klawiatura jak przy przycisku: Enter albo spacja, przytrzymany klawisz raz. Inaczej każdy klawisz
                    // na kafelku z fokusem (np. Tab po złym kliknięciu) byłby kolejną odpowiedzią
                    onKeyDown={(event) => {
                      if (!event.repeat && (event.key === 'Enter' || event.key === ' ')) {
                        handleClick(word.id, word?.term);
                      }
                    }}
                    role="button"
                    tabIndex={0}
                  >
                    {word?.term}
                  </FitText>
                ))}
              </div>
            </div>
          )}
        </div>

        {listEndInfo && (
          <EndGameInfo repeatGameTrigger={repeatGame} nextGame={goToNext} />
        )}

        {!listEndInfo && <UnsplashCredits words={currentWord ? [currentWord] : []} />}

        <RightSideButtons>
          <InfoButton content={<RulesOfTheGame />} />

          {canEditTerms && currentWord && (
            <EditTermButton
              wordId={getListWordId(currentWord)}
              currentTerm={currentWord.term}
              currentDefinition={currentWord.definition}
              listId={activeList?.id || ''}
              onTermUpdated={(newTerm, newDefinition) => {
                const listWordId = getListWordId(currentWord);

                setStateWordsCopy((prev) => prev.map((word) => (getListWordId(word) === listWordId
                  ? { ...word, term: newTerm, definition: newDefinition }
                  : word)));
              }}
            />
          )}

          <PolishAudioButton
            playPolish={playPolish}
            onToggle={togglePlayPolish}
          />

          {canEditTerms && activeList?.id && (
            <EditListButton listId={activeList.id} />
          )}

        </RightSideButtons>
      </FixWidthContent>
    </>
  );
};

export default withTagManager(PickView);
